<script setup>
import { computed, ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import ControlIcon from '@/components/ControlIcon.vue'

const props = defineProps({
  name: {
    type: String,
    default: null
  },
  multiple: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: null
  },
  id: {
    type: String,
    default: null
  },
  autocomplete: {
    type: String,
    default: null
  },
  placeholder: {
    type: String,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  options: {
    type: Array,
    default: null
  },
  type: {
    type: String,
    default: 'text'
  },
  modelValue: {
    type: [String, Number, Boolean, Array, Object],
    default: ''
  },
  region: {
    type: String,
    default: 'text'
  },
  country: {
    type: String,
    default: 'text'
  },
  required: Boolean,
  borderless: Boolean,
  transparent: Boolean,
  ctrlKFocus: Boolean
})

const emit = defineEmits(['update:modelValue', 'right-icon-click'])

const computedValue = computed({
  get: () => props.modelValue,
  set: value => {
    emit('update:modelValue', value)
  }
})

const inputElClass = computed(() => {
  const base = [
    'px-3 py-2 max-w-full focus:ring focus:outline-none border-gray-700 rounded w-full',
    'dark:placeholder-gray-400',
    computedType.value === 'textarea' ? 'h-24' :
      (computedType.value === 'select' && props.multiple) ? 'h-auto min-h-[200px]' : 'h-12',
    props.borderless ? 'border-0' : 'border',
    props.transparent ? 'bg-transparent' : 'bg-white dark:bg-gray-800'
  ]

  if (props.icon) {
    base.push('pl-10')
  }

  if (computedType.value === 'select' && props.multiple) {
    base.push('custom-multiselect')
  }

  return base
})

const computedType = computed(() => props.options ? 'select' : props.type)

const controlIconH = computed(() => props.type === 'textarea' ? 'h-full' : 'h-12')

const store = useStore()

const inputEl = ref(null)

const countrySelector = reactive({
  country: '',
  region: ''
})

if (props.ctrlKFocus) {
  const fieldFocusHook = e => {
    if (e.ctrlKey && e.key === 'k') {
      e.preventDefault()
      inputEl.value.focus()
    } else if (e.key === 'Escape') {
      inputEl.value.blur()
    }
  }

  onMounted(() => {
    if (!store.state.isFieldFocusRegistered) {
      window.addEventListener('keydown', fieldFocusHook)

      store.commit('basic', {
        key: 'isFieldFocusRegistered',
        value: true
      })
    } else {
      console.error('Duplicate field focus event')
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', fieldFocusHook)

    store.commit('basic', {
      key: 'isFieldFocusRegistered',
      value: false
    })
  })
}
</script>

<template>
  <div class="relative">
    <select v-if="computedType === 'select'" :id="id" v-model="computedValue" :name="name" :class="inputElClass"
      :disabled="disabled" :multiple="multiple" :size="multiple ? 8 : 1">
      <option v-for="option in options" :key="option.id ?? option" :value="option" :disabled="disabled">
        {{ option.label ?? option }}
      </option>
    </select>
    <textarea v-else-if="computedType === 'textarea'" :id="id" v-model="computedValue" :class="inputElClass"
      :name="name" :placeholder="placeholder" :required="required" :disabled="disabled" />
    <country-select v-else-if="computedType === 'countryselect'" :class="inputElClass" :name="name" :required="required"
      v-model="computedValue" :country="props.country" topCountry="US" />
    <region-select v-else-if="computedType === 'regionselect'" :class="inputElClass" v-model="computedValue"
      :country="props.country" :region="props.region" />
    <input v-else :id="id" ref="inputEl" v-model="computedValue" :name="name" :autocomplete="autocomplete"
      :required="required" :placeholder="placeholder" :type="computedType" :class="inputElClass" :disabled="disabled">
    <control-icon v-if="icon" :icon="icon" :h="controlIconH" />
  </div>
</template>

<style scoped>
.custom-multiselect {
  padding: 0.5rem;
}

.custom-multiselect option {
  padding: 0.5rem;
  margin: 0.25rem 0;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
}

.custom-multiselect option:checked {
  background: #3b82f6 linear-gradient(0deg, #3b82f6 0%, #3b82f6 100%);
  color: white;
}

.custom-multiselect option:hover {
  background-color: #93c5fd;
  color: #1e3a8a;
}

/* For dark mode */
:deep(.dark) .custom-multiselect option {
  background-color: #1f2937;
  color: #e5e7eb;
}

:deep(.dark) .custom-multiselect option:checked {
  background: #3b82f6 linear-gradient(0deg, #3b82f6 0%, #3b82f6 100%);
  color: white;
}

:deep(.dark) .custom-multiselect option:hover {
  background-color: #374151;
  color: white;
}
</style>